import { createSlice } from "@reduxjs/toolkit";
import { GenericProductsState } from "./genericProducts.state";
import { GenericProductsData, GenericProductsType } from "./genericProducts.types";
import {
  getGenericProducts,
  loadMoreGenericProducts,
} from "./genericProducts.thunk";
import { buildProductsRequestKey } from "./genericProducts.utils";

const initialState: GenericProductsState = {
  data: null,
  status: "idle",
  error: null,
  currentSlug: null,
  currentType: null,
  isPaginating: false,
  ssrHydrated: false,
  lastRequestKey: null,
};

const genericProductsSlice = createSlice({
  name: "genericProducts",
  initialState,
  reducers: {
    resetGenericProducts: () => initialState,
    setInitialGenericProducts: (
      state,
      action: {
        payload: { data: GenericProductsData; slug: string; type: GenericProductsType };
        type: string;
      },
    ) => {
      state.data = action.payload.data;
      state.status = "success";
      state.error = null;
      state.currentSlug = action.payload.slug;
      state.currentType = action.payload.type;
      state.ssrHydrated = true;
      // SSR/ISR verisi filtresiz base listedir; bu dispatch zaten yalnızca
      // URL'de filtre yokken çalışıyor (GenericProductsPage guard'ı).
      state.lastRequestKey = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGenericProducts.pending, (state, action) => {
        const isNew =
          state.currentSlug !== action.meta.arg.slug ||
          state.currentType !== action.meta.arg.type;

        if (isNew) {
          state.data = null;
          state.currentSlug = action.meta.arg.slug;
          state.currentType = action.meta.arg.type;
          state.lastRequestKey = null;
        }

        state.status = "loading";
        state.error = null;
        state.ssrHydrated = false; // first client fetch clears the SSR flag
      })
      .addCase(getGenericProducts.fulfilled, (state, action) => {
        state.status = "success";
        state.data = action.payload.data;
        // Liste hangi filtreyle dolduruldu — remount'ta cache guard bunu okur.
        state.lastRequestKey = buildProductsRequestKey(action.meta.arg.filters);
      })
      .addCase(getGenericProducts.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload ?? "Ürünler yüklenemedi.";
      })
      .addCase(loadMoreGenericProducts.pending, (state) => {
        state.status = "loading";
        state.isPaginating = true;
        state.error = null;
      })
      .addCase(loadMoreGenericProducts.fulfilled, (state, action) => {
        state.status = "success";
        state.isPaginating = false;
        if (state.data) {
          state.data.items = [
            ...state.data.items,
            ...action.payload.data.items,
          ];
          state.data.pagination = action.payload.data.pagination;
        }
      })
      .addCase(loadMoreGenericProducts.rejected, (state, action) => {
        state.status = "error";
        state.isPaginating = false;
        state.error = action.payload ?? "Daha fazla ürün yüklenemedi.";
      });
  },
});

export const { resetGenericProducts, setInitialGenericProducts } = genericProductsSlice.actions;
export default genericProductsSlice.reducer;
